import {
  createStyles,
  makeStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ErrorStatus, SuccessStatus, WarningStatus } from '../../../../icons/StatusIcons';
import { capitaliseFirstLetter } from '../../../../utils';
import { TwilioAPIStatus, ConferencingStatus, TwilioStatus } from '../../../AppStateProvider/AppStateProvider';

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      width: '100%',
      maxWidth: '600px',
    },
    content: {
      [theme.breakpoints.down('sm')]: {
        padding: '2em',
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        marginRight: '0.3em',
      },
    },
  })
);

const serviceStatusObj = {
  operational: { status: 'Up', icon: <SuccessStatus /> },
  major_outage: { status: 'Major Outage', icon: <ErrorStatus /> },
  partial_outage: { status: 'Partial Outage', icon: <WarningStatus /> },
  degraded_performance: { status: 'Degraded', icon: <WarningStatus /> },
};

export function TwilioStatusRow({ status, serviceName }: { status?: TwilioAPIStatus; serviceName: string }) {
  const classes = useStyles();
  const serviceStatus = status ? serviceStatusObj[status] : null;

  return (
    <TableRow>
      <TableCell>
        <div className={classes.iconContainer}>
          {serviceStatus?.icon}
          <Typography variant="body1">
            <strong>{serviceStatus?.status}</strong>
          </Typography>
        </div>
      </TableCell>
      <TableCell>
        <Typography variant="body1">{serviceName}</Typography>
      </TableCell>
    </TableRow>
  );
}

export default function ConferencingStatusRow({
  conferencingStatus,
  apiName,
}: {
  conferencingStatus?: ConferencingStatus | any;
  apiName: string;
}) {
  const classes = useStyles();

  return (
    <TableRow>
      <TableCell>
        <div className={classes.iconContainer}>
          {conferencingStatus === 'operational' ? <SuccessStatus /> : <ErrorStatus />}
          <Typography variant="body1">
            <strong>{conferencingStatus ? capitaliseFirstLetter(conferencingStatus) : 'Down'}</strong>
          </Typography>
        </div>
      </TableCell>
      <TableCell>
        <Typography variant="body1">{apiName}</Typography>
      </TableCell>
    </TableRow>
  );
}

interface ConnectionModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  serviceStatuses: TwilioStatus | null;
  conferencingStatus: ConferencingStatus | null;
  signalingGateway: string;
}

export function ConnectionModal({
  isModalOpen,
  setIsModalOpen,
  serviceStatuses,
  conferencingStatus,
  signalingGateway,
}: ConnectionModalProps) {
  const classes = useStyles();

  return (
    <>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} classes={{ paper: classes.modal }}>
        <DialogTitle disableTypography>
          <Typography variant="h3">Detailed connection information</Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Status</strong>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Type</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TwilioStatusRow status={serviceStatuses?.['Voice REST API US1']} serviceName="Voice REST API" />
              <TwilioStatusRow status={serviceStatuses?.['Conference US1']} serviceName="Conference" />
              <TwilioStatusRow status={serviceStatuses?.['Client Web US1']} serviceName="Client Web" />
              <TwilioStatusRow status={serviceStatuses?.['Recording US1']} serviceName="Recording" />
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    {signalingGateway === 'Reachable' ? <SuccessStatus /> : <ErrorStatus />}
                    <Typography variant="body1">
                      <strong>{signalingGateway}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">Signaling Gateway</Typography>
                </TableCell>
              </TableRow>
              <ConferencingStatusRow
                conferencingStatus={conferencingStatus?.['conferenceApiStatus']}
                apiName="Conference API"
              />
              <ConferencingStatusRow
                conferencingStatus={conferencingStatus?.['bookingApiStatus']}
                apiName="Booking API"
              />
              <ConferencingStatusRow
                conferencingStatus={conferencingStatus?.['phoneNumberApiStatus']}
                apiName="Phone Number API"
              />
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}
